import React, { useState, useEffect } from 'react';
import { User, Lock, PlayCircle, ArrowLeft, Mail, Eye, EyeOff, Calendar, Shield, Zap } from 'lucide-react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { APP_CONFIG } from '../config/theme';

const AuthPage = ({ onUserAuthenticated, onNavigate, onDemoLogin, initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode !== 'cadastro');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Atualizar modo quando initialMode mudar
  useEffect(() => {
    setIsLogin(initialMode !== 'cadastro');
  }, [initialMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (!auth) {
        throw new Error("Sistema de autenticação não está configurado.");
      }

      if (isForgotPassword) {
        await sendPasswordResetEmail(auth, formData.email);
        setSuccess('Email de recuperação enviado! Verifique sua caixa de entrada.');
        return;
      }

      if (isLogin) {
        const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        onUserAuthenticated(userCredential.user);
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        if (formData.fullName) {
          await updateProfile(userCredential.user, { displayName: formData.fullName });
        }
        onUserAuthenticated(userCredential.user);
      }
    } catch (err) {
      console.error("❌ Erro de autenticação:", err);
      
      const errorMessages = {
        'auth/user-not-found': 'Usuário não encontrado. Verifique o e-mail ou cadastre-se.',
        'auth/wrong-password': 'Senha incorreta. Tente novamente.',
        'auth/invalid-credential': 'Email ou senha incorretos.',
        'auth/email-already-in-use': 'Este e-mail já está em uso. Tente fazer login.',
        'auth/weak-password': 'Senha muito fraca. Use pelo menos 6 caracteres.',
        'auth/invalid-email': 'E-mail inválido.',
        'auth/too-many-requests': 'Muitas tentativas. Aguarde alguns minutos.'
      };
      
      setError(errorMessages[err.code] || 'Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setIsForgotPassword(false);
    setError(null);
    setSuccess(null);
    setFormData({ email: '', password: '', fullName: '' });
  };

  const toggleForgotPassword = () => {
    setIsForgotPassword(!isForgotPassword);
    setError(null);
    setSuccess(null);
  };

  const benefits = [
    { icon: Calendar, text: 'Controle total de prazos' },
    { icon: Zap, text: 'IA detecta prazos automaticamente' },
    { icon: Shield, text: 'Seus dados 100% seguros' }
  ];

  return (
    <div className="min-h-screen flex">
      {/* Lado Esquerdo - Benefícios (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          {/* Logo */}
          <button 
            onClick={() => onNavigate && onNavigate('landing')}
            className="flex items-center gap-3 text-white hover:opacity-80 transition-opacity"
          >
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold">{APP_CONFIG.name}</span>
          </button>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-4">
              {isLogin ? 'Bem-vindo de volta!' : 'Comece agora gratuitamente'}
            </h1>
            <p className="text-blue-100 text-lg">
              {isLogin 
                ? 'Acesse sua conta e continue gerenciando seus prazos processuais com segurança.'
                : 'Junte-se a centenas de advogados que nunca mais perderam um prazo.'}
            </p>
          </div>

          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-4 text-white/90">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <benefit.icon className="w-5 h-5" />
                </div>
                <span className="font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-blue-200 text-sm">
            © 2026 {APP_CONFIG.name}. Todos os direitos reservados.
          </p>
        </div>
      </div>

      {/* Lado Direito - Formulário */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="w-full max-w-md">
          {/* Botão Voltar (Mobile) */}
          <button
            onClick={() => onNavigate && onNavigate('landing')}
            className="lg:hidden inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 mb-6"
          >
            <ArrowLeft size={16} />
            Voltar
          </button>

          {/* Card do Formulário */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {/* Header */}
            <div className="text-center mb-8">
              {/* Logo Mobile */}
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-800">{APP_CONFIG.name}</span>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {isForgotPassword 
                  ? 'Recuperar Senha' 
                  : isLogin 
                    ? 'Entrar na sua conta' 
                    : 'Criar conta gratuita'}
              </h2>
              <p className="text-gray-500">
                {isForgotPassword 
                  ? 'Enviaremos um link para seu email'
                  : isLogin 
                    ? 'Preencha seus dados para acessar' 
                    : 'Preencha seus dados para começar'}
              </p>
            </div>

            {/* Alertas */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                {success}
              </div>
            )}

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nome (só no cadastro) */}
              {!isLogin && !isForgotPassword && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Dr. João da Silva"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-mail
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="seu@email.com"
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Senha */}
              {!isForgotPassword && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {isLogin && (
                    <button 
                      type="button"
                      onClick={toggleForgotPassword}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Esqueci minha senha
                    </button>
                  )}
                </div>
              )}

              {/* Botão Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Aguarde...
                  </span>
                ) : (
                  isForgotPassword 
                    ? 'Enviar Email' 
                    : isLogin 
                      ? 'Entrar' 
                      : 'Criar Minha Conta'
                )}
              </button>

              {/* Voltar do Forgot Password */}
              {isForgotPassword && (
                <button
                  type="button"
                  onClick={toggleForgotPassword}
                  className="w-full py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                >
                  Voltar ao Login
                </button>
              )}
            </form>

            {/* Divisor */}
            {!isForgotPassword && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">ou</span>
                  </div>
                </div>

                {/* Botão Demo */}
                <button
                  onClick={onDemoLogin}
                  disabled={loading}
                  className="w-full py-3 px-4 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-2"
                >
                  <PlayCircle size={20} />
                  Experimentar Demo (sem cadastro)
                </button>
              </>
            )}

            {/* Toggle Login/Cadastro */}
            {!isForgotPassword && (
              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  {isLogin ? 'Não tem uma conta?' : 'Já tem uma conta?'}
                  <button 
                    onClick={toggleMode}
                    className="ml-2 font-semibold text-blue-600 hover:text-blue-700"
                    disabled={loading}
                  >
                    {isLogin ? 'Cadastre-se grátis' : 'Faça Login'}
                  </button>
                </p>
              </div>
            )}
          </div>

          {/* Links Legais */}
          <p className="mt-6 text-center text-xs text-gray-500">
            Ao continuar, você concorda com nossos{' '}
            <button onClick={() => onNavigate('termos')} className="underline hover:text-gray-700">
              Termos de Uso
            </button>
            {' '}e{' '}
            <button onClick={() => onNavigate('privacidade')} className="underline hover:text-gray-700">
              Política de Privacidade
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
